import { Box, Chip, Divider, MenuItem, Paper, TextField } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import { FieldEvidenceButton } from '../../../../components/evidence/FieldEvidenceButton';
import type { SpecimenPreview } from '../computations';
import { SPECIMEN_CONDITION_OPTIONS } from '../rockUcsFields';
import { DerivedValue, fmt, inputSx, measurementRowSx, paperSx, SectionHeader, unit } from '../sectionStyles';

/**
 * Structural view of the fields this section edits (BUG-2026-0138 item 12):
 * RockUCSGeneral satisfies it directly; the UCS-NS page satisfies it with a
 * merged specimen-1 + general view model, so the section is shared, not cloned.
 */
export interface SpecimenParameterFields {
  diameter1: number | null;
  diameter2: number | null;
  diameter3: number | null;
  diameter4: number | null;
  diameter5: number | null;
  diameter6: number | null;
  length1: number | null;
  length2: number | null;
  length3: number | null;
  length4: number | null;
  massG: number | null;
  leebHardnessEnds: number | null;
  leebHardnessEdge: number | null;
  specimenCondition: string | null;
  preparedBy: string | null;
}

/** Preview values this section displays (SpecimenPreview minus gauge length). */
export type SpecimenParameterPreview = Omit<SpecimenPreview, 'avgGaugeLengthMm'>;

interface Props {
  general: SpecimenParameterFields;
  preview: SpecimenParameterPreview;
  onChange: (field: keyof SpecimenParameterFields, value: number | string | null) => void;
  readOnly?: boolean;
}

/**
 * REQ-03 — Specimen Parameters: 6 diameters → avg, 4 lengths → avg, L/D check,
 * as-received mass, bulk density, Leeb hardness (ends/edge), prepared-by.
 * Wet/dry mass, water content, and dry density are intentionally absent (D5).
 *
 * BUG-2026-0137 §3/§4: Specimen Condition moved here (the Test/Sample Summary
 * section is gone), and the measurements are ONE ROW of diameters (D1…D6) over
 * ONE ROW of lengths (L1…L4) so tabbing flows D1→D6 then L1→L4.
 */
export const SpecimenParametersSection = ({ general, preview, onChange, readOnly }: Props) => {
  const num = (field: keyof SpecimenParameterFields) => (value: number | null) => onChange(field, value);

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label="1. Specimen Parameters" />

      {/* One row of diameters, one row of lengths (§4 — natural tab order).
          Each measurement row carries its field-evidence camera button. */}
      <Box sx={{ display: 'grid', rowGap: 1.25, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ ...measurementRowSx, flex: 1 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <NumericTextField
                key={`d${i}`}
                size="small"
                label={`Dia ${i}`}
                value={general[`diameter${i}` as keyof SpecimenParameterFields] as number | null}
                onChange={num(`diameter${i}` as keyof SpecimenParameterFields)}
                InputProps={{ endAdornment: unit('mm'), readOnly }}
                sx={inputSx}
              />
            ))}
          </Box>
          <FieldEvidenceButton fieldKey="diameters" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ ...measurementRowSx, flex: 1 }}>
            {[1, 2, 3, 4].map((i) => (
              <NumericTextField
                key={`l${i}`}
                size="small"
                label={`Len ${i}`}
                value={general[`length${i}` as keyof SpecimenParameterFields] as number | null}
                onChange={num(`length${i}` as keyof SpecimenParameterFields)}
                InputProps={{ endAdornment: unit('mm'), readOnly }}
                sx={inputSx}
              />
            ))}
          </Box>
          <FieldEvidenceButton fieldKey="lengths" />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
        {/* Condition / mass / Leeb / prepared-by */}
        <Box sx={{ display: 'grid', rowGap: 1.25, alignContent: 'start' }}>
          <TextField
            select size="small" label="Specimen Condition"
            value={general.specimenCondition ?? ''}
            onChange={(e) => onChange('specimenCondition', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          >
            {SPECIMEN_CONDITION_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <NumericTextField
              size="small" label="Mass (as received)"
              value={general.massG} onChange={num('massG')}
              InputProps={{ endAdornment: unit('g'), readOnly }} sx={{ ...inputSx, flex: 1 }}
            />
            <FieldEvidenceButton fieldKey="massG" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <NumericTextField
              size="small" label="Leeb Hardness — Ends"
              value={general.leebHardnessEnds} onChange={num('leebHardnessEnds')}
              InputProps={{ endAdornment: unit('HLD'), readOnly }} sx={{ ...inputSx, flex: 1 }}
            />
            {/* One evidence slot covers both Leeb readings (ends + edge) */}
            <FieldEvidenceButton fieldKey="leebHardness" />
          </Box>
          <NumericTextField
            size="small" label="Leeb Hardness — Edge"
            value={general.leebHardnessEdge} onChange={num('leebHardnessEdge')}
            InputProps={{ endAdornment: unit('HLD'), readOnly }} sx={inputSx}
          />
          <TextField
            size="small" label="Prepared By (initials)"
            value={general.preparedBy ?? ''}
            onChange={(e) => onChange('preparedBy', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        </Box>

        {/* Derived / calculated */}
        <Box sx={{ display: 'grid', rowGap: 0.5, alignContent: 'start', px: 1 }}>
          <SectionHeader label="Derived / Calculated" color="text.secondary" />
          <DerivedValue label="Avg Diameter" value={fmt(preview.avgDiameterMm, 3)} suffix="mm" />
          <DerivedValue label="Avg Length" value={fmt(preview.avgLengthMm, 3)} suffix="mm" />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <DerivedValue label="L/D Ratio" value={fmt(preview.lOverD, 2)} />
            {preview.lOverDPass !== null && (
              <Chip
                size="small"
                label={preview.lOverDPass ? 'Pass' : 'Fail'}
                color={preview.lOverDPass ? 'success' : 'error'}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <DerivedValue label="Area (internal)" value={fmt(preview.areaMm2, 1)} suffix="mm²" />
          <DerivedValue label="Volume (internal)" value={fmt(preview.volumeCm3, 2)} suffix="cm³" />
          <DerivedValue label="Bulk Density ρb" value={fmt(preview.bulkDensityGCm3, 2)} suffix="g/cm³" />
        </Box>
      </Box>
    </Paper>
  );
};
